import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import EmpresaTab from './components/EmpresaTab';
import IdentidadTab from './components/IdentidadTab';
import TextosTab from './components/TextosTab';
import VistaA4Tab from './components/VistaA4Tab';
import PapeleriaTab from './components/PapeleriaTab';
import {
  getBranding,
  saveBranding,
  uploadLogo,
  uploadHeaderImage,
  uploadLetterhead,
  uploadBusinessCard,
} from '../../services/companyBrandingService';

const TABS = [
  { id: 'empresa', label: 'Datos de la empresa', icon: 'Building2' },
  { id: 'identidad', label: 'Identidad visual', icon: 'Palette' },
  { id: 'papeleria', label: 'Papelería', icon: 'FileStack' },
  { id: 'textos', label: 'Textos legales', icon: 'FileText' },
  { id: 'preview', label: 'Vista previa A4', icon: 'Eye' },
];

const DEFAULT_FORM = {
  business_name: '',
  fiscal_name: '',
  rfc: '',
  address: '',
  whatsapp: '',
  phone: '',
  email: '',
  website: '',
  facebook_name: '',
  instagram_name: '',
  facebook_url: '',
  instagram_url: '',
  primary_color: '#E53E3E',
  secondary_color: '#1A1A1A',
  accent_color: '#718096',
  logo_url: '',
  header_image_url: '',
  letterhead_url: '',
  business_card_url: '',
  selected_proposal: 'none',
  has_custom_papeleria: false,
  billing_rfc: '',
  billing_fiscal_name: '',
  billing_address: '',
  billing_regime: '',
  billing_cfdi_use: '',
  footer_text: '',
  legal_text: '',
  quote_terms: '',
  warranty_text: '',
};

const CompanyBranding = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('empresa');
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadingLetterhead, setUploadingLetterhead] = useState(false);
  const [uploadingBusinessCard, setUploadingBusinessCard] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  const companyId = userProfile?.company_id;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchBranding = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getBranding(companyId);
      if (data) {
        setForm({ ...DEFAULT_FORM, ...data });
      }
    } catch (err) {
      setError('No se pudo cargar la configuración de identidad.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!companyId) return;
    setSaving(true);
    try {
      const saved = await saveBranding(companyId, form);
      setForm((prev) => ({ ...prev, ...saved }));
      showToast('Configuración guardada correctamente');
    } catch (err) {
      showToast('Error al guardar la configuración. Intenta de nuevo.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadLogo = async (file) => {
    if (!companyId) return;
    setUploadingLogo(true);
    try {
      const url = await uploadLogo(companyId, file);
      setForm((prev) => ({ ...prev, logo_url: url }));
      showToast('Logotipo subido correctamente');
    } catch (err) {
      showToast('Error al subir el logotipo.', 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleUploadHeader = async (file) => {
    if (!companyId) return;
    setUploadingHeader(true);
    try {
      const url = await uploadHeaderImage(companyId, file);
      setForm((prev) => ({ ...prev, header_image_url: url }));
      showToast('Imagen de cabecera subida correctamente');
    } catch (err) {
      showToast('Error al subir la imagen de cabecera.', 'error');
    } finally {
      setUploadingHeader(false);
    }
  };

  const handleUploadLetterhead = async (file) => {
    if (!companyId) return;
    setUploadingLetterhead(true);
    try {
      const url = await uploadLetterhead(companyId, file);
      setForm((prev) => ({ ...prev, letterhead_url: url, has_custom_papeleria: true, selected_proposal: 'none' }));
      showToast('Hoja membretada subida correctamente. Esta será la plantilla oficial para todos los documentos.');
    } catch (err) {
      showToast('Error al subir la hoja membretada.', 'error');
    } finally {
      setUploadingLetterhead(false);
    }
  };

  const handleUploadBusinessCard = async (file) => {
    if (!companyId) return;
    setUploadingBusinessCard(true);
    try {
      const url = await uploadBusinessCard(companyId, file);
      setForm((prev) => ({ ...prev, business_card_url: url }));
      showToast('Tarjeta de presentación subida correctamente');
    } catch (err) {
      showToast('Error al subir la tarjeta de presentación.', 'error');
    } finally {
      setUploadingBusinessCard(false);
    }
  };

  const handleRemoveLetterhead = () => {
    setForm((prev) => ({ ...prev, letterhead_url: '', has_custom_papeleria: false }));
    showToast('Hoja membretada eliminada. Se usará la propuesta seleccionada.');
  };

  const handleRemoveBusinessCard = () => {
    setForm((prev) => ({ ...prev, business_card_url: '' }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="text-sm text-muted-foreground">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
            toast?.type === 'error' ?'bg-destructive text-destructive-foreground' :'bg-success text-success-foreground'
          }`}
        >
          <Icon name={toast?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={16} />
          {toast?.message}
        </div>
      )}

      {/* Page header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-card">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/workshop-dashboard')}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mr-1"
          >
            <Icon name="ArrowLeft" size={16} />
            <span>Dashboard</span>
          </button>
          <div className="w-px h-5 bg-border" />
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Palette" size={18} className="text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-heading font-semibold text-foreground">Identidad y Papelería</h1>
            <p className="text-xs text-muted-foreground">Configura la imagen corporativa de tu empresa</p>
          </div>
        </div>
        <Button onClick={handleSave} loading={saving} iconName="Save" disabled={!companyId}>
          Guardar configuración
        </Button>
      </div>

      {error && (
        <div className="mx-6 mt-4 flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          <Icon name="AlertCircle" size={16} />
          {error}
        </div>
      )}

      {!companyId && (
        <div className="mx-6 mt-4 flex items-center gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20 text-warning text-sm">
          <Icon name="AlertTriangle" size={16} />
          No se encontró una empresa asociada a tu cuenta.
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border bg-card px-6">
        <div className="flex gap-0 overflow-x-auto">
          {TABS?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Icon name={tab?.icon} size={15} />
              {tab?.label}
              {tab?.id === 'papeleria' && form?.letterhead_url && (
                <span className="ml-1 w-2 h-2 rounded-full bg-success inline-block" title="Papelería personalizada activa" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto">
          {activeTab === 'empresa' && (
            <EmpresaTab form={form} onChange={handleFieldChange} />
          )}
          {activeTab === 'identidad' && (
            <IdentidadTab
              form={form}
              onChange={handleFieldChange}
              onUploadLogo={handleUploadLogo}
              onUploadHeader={handleUploadHeader}
              uploadingLogo={uploadingLogo}
              uploadingHeader={uploadingHeader}
            />
          )}
          {activeTab === 'papeleria' && (
            <PapeleriaTab
              form={form}
              onChange={handleFieldChange}
              onUploadLetterhead={handleUploadLetterhead}
              onUploadBusinessCard={handleUploadBusinessCard}
              onRemoveLetterhead={handleRemoveLetterhead}
              onRemoveBusinessCard={handleRemoveBusinessCard}
              uploadingLetterhead={uploadingLetterhead}
              uploadingBusinessCard={uploadingBusinessCard}
            />
          )}
          {activeTab === 'textos' && (
            <TextosTab form={form} onChange={handleFieldChange} />
          )}
          {activeTab === 'preview' && (
            <VistaA4Tab form={form} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyBranding;
